import { useCallback, useRef, useState } from 'react'
import { useErrorLine } from '../error/use-error-line'
import { cursorPager } from './cursor-pager'

// A TableAgInfinite list whose server pages by cursor (cursor-pager.js), as
// a page holds it: the grid's datasource, the reload that starts it again at
// the top, the refresh that reads its loaded blocks again in place, the
// grid's search sent as `q`, the failed read's line, and the total once it
// is known. Used by the AI tools telemetry lists (markable-list.js) and the
// paged recycle bin (shared/recycle/recycle-paged-list.js).
//
// useCursorList({ fetchPage, toRow, filters, failedText, orderField, blocked })
//   fetchPage(params) -> { items, next }; toRow(item) -> a row; filters: the
//   server's query (memoised by the page); failedText: the sentence for a
//   failed read; orderField: the one column whose sort asks the server for
//   ?order=oldest|newest; blocked: a reason nothing can be asked (a date
//   range that can't be), or null.
// -> list:
//   getRows, reloadKey, refreshKey: for the grid (onGetRows, reloadKey,
//     refreshKey). The grid's datasource keeps the onGetRows it was ready
//     with, so getRows reads the latest pager through a ref.
//   search: the search the grid last asked with ('' for none, trimmed).
//   total: how many rows the filters (and search) match, once the grid has
//     read to the end of the list (a list of up to a block, at once); null
//     while unknown: a longer list's first block, a failed read, blocked.
//   error, failure, setError, retry(): the failed read's line, Try again.
//   change(set) wraps a filter's setter: it also clears a failed read's
//     line, so the new filter's list is asked for.
//   reloadFromTop(): rows changed in ways that may move many (a bulk
//     write): the grid starts again from the top, with a new pager.
//   refreshInPlace(): rows changed (a mark): the grid reads its loaded
//     blocks again where it is scrolled, with the same pager (its block
//     cursors).
//
// The pager (its block -> cursor map) is made when the grid asks and the
// filters, the search, the order or the reload count differ from the
// pager's: a cursor belongs to one order and one query.
export const useCursorList = ({ fetchPage, toRow, filters, failedText, orderField, blocked }) => {
  const [reload, setReload] = useState(0)
  const [refresh, setRefresh] = useState(0)
  const [error, failure, setError] = useErrorLine()
  const [counted, setCounted] = useState(null) // { key, count }
  const [search, setSearch] = useState('')

  const latest = useRef()
  latest.current = { fetchPage, toRow, filters, setError, failedText, reload, orderField, blocked }
  const made = useRef({ key: null, pager: null })
  const getRows = useCallback((start, end, asked, sortCol, sortDir) => {
    const now = latest.current
    if (now.blocked) return Promise.resolve({ data: [], count: 0 })
    const q = String(asked || '').trim()
    setSearch(q)
    const order = now.orderField && sortCol === now.orderField ? (sortDir === 'asc' ? 'oldest' : sortDir === 'desc' ? 'newest' : '') : ''
    const counts = `${JSON.stringify(now.filters)}|${q}|${now.reload}`
    const key = `${counts}|${order}`
    if (made.current.key !== key) {
      const mine = { key, counts, failed: false }
      const query = { ...now.filters, ...(q ? { q } : {}), ...(order ? { order } : {}) }
      mine.pager = cursorPager(now.fetchPage, now.toRow, query, (message, err) => { mine.failed = true; now.setError(message, err) }, now.failedText)
      made.current = mine
    }
    const pager = made.current
    return pager.pager(start, end).then(result => {
      // the end of the list reached: its count is the query's total
      if (made.current === pager && !pager.failed && result && result.count >= 0) setCounted({ key: pager.counts, count: result.count })
      return result
    })
  }, [])

  const reloadKey = `${JSON.stringify(filters)}:${reload}`
  const total = !blocked && !error && counted && counted.key === `${JSON.stringify(filters)}|${search}|${reload}` ? counted.count : null
  const change = set => value => { setError(null); set(value) }
  const retry = () => { setError(null); setReload(n => n + 1) }
  const reloadFromTop = useCallback(() => setReload(n => n + 1), [])
  const refreshInPlace = useCallback(() => setRefresh(n => n + 1), [])

  return { getRows, reloadKey, refreshKey: refresh, total, search, error, failure, setError, retry, change, reloadFromTop, refreshInPlace, blocked }
}
